

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';

const INSPECTION_AREAS = [
  { id: 'bedline', name: 'Bedline', icon: '🛏️', required: true },
  { id: 'tv_unit', name: 'TV Unit', icon: '📺', required: true },
  { id: 'curtains', name: 'Curtains', icon: '🪟', required: true },
  { id: 'coffee_maker', name: 'Coffee Maker', icon: '☕', required: true },
  { id: 'fridge', name: 'Fridge', icon: '🧊', required: true },
  { id: 'couch', name: 'Couch', icon: '🛋️', required: true },
  { id: 'iron_unit', name: 'Iron Unit', icon: '👕', required: true },
  { id: 'towels', name: 'Towels', icon: '🧣', required: true },
  { id: 'dental', name: 'Dental Kit', icon: '🪥', required: true },
  { id: 'toiletries', name: 'Toiletries', icon: '🧴', required: true },
  { id: 'neatness', name: 'Neatness', icon: '✨', required: true },
];

const DAMAGE_PATTERNS = {
  bedline: ['stains', 'tears', 'wrinkles', 'discoloration'],
  tv_unit: ['cracks', 'scratches', 'dead pixels', 'broken screen'],
  curtains: ['tears', 'stains', 'missing hooks', 'fading', 'rod damage', 'uneven hanging'],
  coffee_maker: ['cracks', 'leaks', 'mineral buildup', 'broken handle'],
  fridge: ['dents', 'leaks', 'rust', 'broken seal', 'ice buildup'],
  couch: ['stains', 'tears', 'sagging', 'pet damage', 'burn marks', 'loose cushions', 'broken springs'],
  iron_unit: ['scratches', 'rust', 'broken cord', 'mineral buildup', 'cracked handle', 'non-functional'],
  towels: ['stains', 'tears', 'fraying', 'discoloration', 'mold spots', 'thin/worn areas'],
  dental: ['tampered', 'expired', 'missing items'],
  toiletries: ['leaks', 'empty bottles', 'expired', 'contaminated'],
  neatness: ['dust', 'debris', 'unmade bed', 'clutter', 'trash']
};

const PRECONFIGURED_ISSUES = {
  curtains: {
    hasDamage: true,
    severity: 'moderate',
    issues: ['missing hooks', 'tears', 'uneven hanging'],
    recommendation: '⚠️ MODERATE DAMAGE: Curtains have multiple missing hooks and visible tears. Please replace curtain hooks and repair tears.'
  },
  iron_unit: {
    hasDamage: true,
    severity: 'severe',
    issues: ['cracked handle', 'non-functional', 'mineral buildup'],
    recommendation: '🔴 SEVERE DAMAGE: Iron unit has cracked handle and appears non-functional. Immediate replacement required.'
  },
  towels: {
    hasDamage: true,
    severity: 'moderate',
    issues: ['fraying', 'stains', 'thin/worn areas'],
    recommendation: '⚠️ MODERATE DAMAGE: Towels show significant fraying edges, multiple stains, and thinning fabric.'
  },
  couch: {
    hasDamage: true,
    severity: 'severe',
    issues: ['stains', 'tears', 'sagging', 'broken springs'],
    recommendation: '🔴 SEVERE DAMAGE: Couch has multiple issues including visible tears, deep stains, sagging cushions, and broken springs.'
  }
};

export default function AuditReport({ roomData, checklistData, damageReports, onFinish }) {
  const [photos, setPhotos] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [processingImages, setProcessingImages] = useState({});
  const [damageAnalysis, setDamageAnalysis] = useState({});
  const [showAnalysisTable, setShowAnalysisTable] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [savedScreenshots, setSavedScreenshots] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showScreenshotGallery, setShowScreenshotGallery] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [screenshotError, setScreenshotError] = useState(null);
  const [uploadTriggered, setUploadTriggered] = useState(false);
  
  const dashboardRef = useRef(null);

  const problematicAreas = ['curtains', 'iron_unit', 'towels', 'couch'];
  const [issueResolutions, setIssueResolutions] = useState({
    curtains: { acknowledged: false, notes: '' },
    iron_unit: { acknowledged: false, notes: '' },
    towels: { acknowledged: false, notes: '' },
    couch: { acknowledged: false, notes: '' }
  });

  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Memoized calculations
  const allPhotosCaptured = useMemo(() => 
    INSPECTION_AREAS.every(area => photos[area.id]), 
    [photos]
  );
  
  const allPhotosProcessed = useMemo(() => 
    Object.keys(photos).length > 0 && 
    Object.keys(photos).every(areaId => !processingImages[areaId]),
    [photos, processingImages]
  );
  
  const hasDamagedItems = useMemo(() => 
    Object.values(damageAnalysis).some(analysis => analysis?.status === 'damaged'),
    [damageAnalysis]
  );
  
  const damagedItemsCount = useMemo(() => 
    Object.values(damageAnalysis).filter(analysis => analysis?.status === 'damaged').length,
    [damageAnalysis]
  );
  
  const hasValidationErrors = useMemo(() => 
    Object.values(validationErrors).some(error => error !== null),
    [validationErrors]
  );
  
  const problematicPhotosCaptured = useMemo(() => 
    problematicAreas.every(area => photos[area]),
    [photos]
  );
  
  const allProblematicAreasAnalyzed = useMemo(() => 
    problematicAreas.every(area => damageAnalysis[area]),
    [damageAnalysis]
  );
  
  const allIssuesAcknowledged = useCallback(() => {
    return problematicAreas.every(area => issueResolutions[area]?.acknowledged === true);
  }, [issueResolutions]);
  
  // Calculate which issues are still pending
  const pendingAcknowledgments = useMemo(() => {
    return problematicAreas.filter(area => !issueResolutions[area]?.acknowledged);
  }, [issueResolutions]);
  
  const isReadyForUpload = useMemo(() => {
    const ready = allPhotosCaptured && 
                  allPhotosProcessed && 
                  !hasValidationErrors && 
                  allIssuesAcknowledged() && 
                  problematicPhotosCaptured && 
                  allProblematicAreasAnalyzed;
    
    console.log('Upload Readiness Check:', {
      allPhotosCaptured,
      allPhotosProcessed,
      hasValidationErrors,
      allIssuesAcknowledged: allIssuesAcknowledged(),
      problematicPhotosCaptured,
      allProblematicAreasAnalyzed,
      isReady: ready,
      pendingAcknowledgments: pendingAcknowledgments
    });
    
    return ready;
  }, [
    allPhotosCaptured, 
    allPhotosProcessed, 
    hasValidationErrors, 
    allIssuesAcknowledged, 
    problematicPhotosCaptured, 
    allProblematicAreasAnalyzed,
    pendingAcknowledgments
  ]);

  // Function to send acknowledgment to manager
  const sendAcknowledgementToManager = async (areaId, resolution) => {
    const notificationData = {
      type: 'DAMAGE_ACKNOWLEDGEMENT',
      areaId,
      areaName: INSPECTION_AREAS.find(a => a.id === areaId)?.name,
      roomNumber: roomData?.roomId,
      timestamp: new Date().toISOString(),
      resolution: resolution,
      detectedIssues: damageAnalysis[areaId]?.detectedIssues || [],
      severity: damageAnalysis[areaId]?.severity || 'unknown',
      staffId: 'HK1001',
      status: 'PENDING_MAINTENANCE'
    };
    
    // Store in localStorage for manager view
    const existingNotifications = JSON.parse(localStorage.getItem('manager_notifications') || '[]');
    existingNotifications.unshift(notificationData);
    localStorage.setItem('manager_notifications', JSON.stringify(existingNotifications));
    
    // In a real app, you would send to API
    console.log('📧 Notification sent to Manager:', notificationData);
    
    return notificationData;
  };

  // Upload function
  const performUpload = async () => {
    setIsUploading(true);
    
    try {
      // Simulate API upload delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Prepare upload data
      const uploadPayload = {
        roomId: roomData?.roomId,
        roomNumber: roomData?.roomId,
        timestamp: new Date().toISOString(),
        staffId: 'HK1001',
        photos: Object.entries(photos).map(([areaId, photoData]) => ({
          areaId,
          areaName: INSPECTION_AREAS.find(a => a.id === areaId)?.name,
          timestamp: photoData.timestamp,
          photoUrl: photoData.url
        })),
        damageAnalysis: Object.values(damageAnalysis),
        issueResolutions: issueResolutions,
        acknowledgedIssues: problematicAreas.map(area => ({
          areaId: area,
          areaName: INSPECTION_AREAS.find(a => a.id === area)?.name,
          acknowledged: issueResolutions[area]?.acknowledged,
          notes: issueResolutions[area]?.notes,
          timestamp: new Date().toISOString()
        })),
        statistics: {
          totalAreas: INSPECTION_AREAS.length,
          capturedAreas: Object.keys(photos).length,
          damagedItems: damagedItemsCount,
          acknowledgedIssues: Object.values(issueResolutions).filter(r => r.acknowledged).length,
          totalProblematicAreas: problematicAreas.length,
          averageConfidence: Object.values(damageAnalysis).reduce((acc, curr) => acc + (curr?.confidence || 0), 0) / (Object.keys(damageAnalysis).length || 1)
        },
        status: 'COMPLETED',
        managerNotificationSent: true
      };
      
      // Save to localStorage for backup
      localStorage.setItem(`audit_upload_${roomData?.roomId}_${Date.now()}`, JSON.stringify(uploadPayload));
      
      setUploadStatus({ 
        success: true, 
        message: `✅ All data synced to server. Manager has been notified of all acknowledged issues at ${new Date().toLocaleTimeString()}` 
      });
      
      showToast('✅ Data uploaded and manager notified successfully!', 'success');
      
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus({ 
        success: false, 
        message: '❌ Upload failed. Please try again.' 
      });
      showToast('❌ Upload failed. Please try again.', 'error');
      setUploadTriggered(false);
    } finally {
      setIsUploading(false);
    }
  };

  // Auto-upload when all conditions are met
  useEffect(() => {
    if (isReadyForUpload && !isUploading && !uploadStatus.success && !uploadTriggered) {
      console.log('All issues acknowledged! Triggering upload to manager...');
      setUploadTriggered(true);
      performUpload();
    }
  }, [isReadyForUpload, isUploading, uploadStatus.success, uploadTriggered]);

  // Reset upload triggered when photos or analysis changes
  useEffect(() => {
    if (!isReadyForUpload && uploadTriggered) {
      setUploadTriggered(false);
    }
  }, [isReadyForUpload, uploadTriggered]);

  const convertBlobUrlsToDataUrls = async () => {
    const imageElements = dashboardRef.current.querySelectorAll('img');
    const conversions = [];
    
    for (const img of imageElements) {
      if (img.src && img.src.startsWith('blob:')) {
        try {
          const response = await fetch(img.src);
          const blob = await response.blob();
          const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          
          img.setAttribute('data-original-src', img.src);
          img.src = dataUrl;
          conversions.push(img);
        } catch (err) {
          console.warn('Failed to convert image:', img.src, err);
          img.style.visibility = 'hidden';
          img.setAttribute('data-hidden', 'true');
          conversions.push(img);
        }
      }
    }
    
    return conversions;
  };

  const restoreOriginalImages = (convertedImages) => {
    for (const img of convertedImages) {
      const originalSrc = img.getAttribute('data-original-src');
      if (originalSrc) {
        img.src = originalSrc;
        img.removeAttribute('data-original-src');
      }
      if (img.getAttribute('data-hidden') === 'true') {
        img.style.visibility = 'visible';
        img.removeAttribute('data-hidden');
      }
    }
  };

  const captureScreenshot = async () => {
    if (!dashboardRef.current) {
      showToast('Dashboard not ready', 'error');
      return;
    }
    
    setIsCapturing(true);
    setScreenshotError(null);
    let convertedImages = [];
    
    try {
      convertedImages = await convertBlobUrlsToDataUrls();
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const element = dashboardRef.current;
      const originalWidth = element.style.width;
      const originalHeight = element.style.height;
      
      element.style.width = `${element.scrollWidth}px`;
      element.style.height = `${element.scrollHeight}px`;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#f3f4f6',
        logging: false,
        useCORS: true,
        allowTaint: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (clonedDoc, elementClone) => {
          clonedDoc.body.style.backgroundColor = '#f3f4f6';
          clonedDoc.body.style.margin = '0';
          clonedDoc.body.style.padding = '20px';
          
          const clonedImages = clonedDoc.querySelectorAll('img');
          clonedImages.forEach((img, index) => {
            if (convertedImages[index] && convertedImages[index].src) {
              img.src = convertedImages[index].src;
            }
            img.style.visibility = 'visible';
            img.style.opacity = '1';
          });
        }
      });
      
      element.style.width = originalWidth;
      element.style.height = originalHeight;
      
      const screenshotUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      const screenshotData = {
        id: Date.now(),
        url: screenshotUrl,
        timestamp: new Date().toISOString(),
        roomNumber: roomData?.roomId,
        damagedItems: damagedItemsCount,
        totalAreas: Object.keys(photos).length
      };
      
      setSavedScreenshots(prev => [screenshotData, ...prev]);
      
      const existingScreenshots = JSON.parse(localStorage.getItem('audit_screenshots') || '[]');
      localStorage.setItem('audit_screenshots', JSON.stringify([screenshotData, ...existingScreenshots]));
      
      showToast('✅ Screenshot saved successfully!', 'success');
      
    } catch (error) {
      console.error('Screenshot capture failed:', error);
      setScreenshotError('Failed to capture screenshot. Please try again.');
      showToast('❌ Failed to capture screenshot. Please try again.', 'error');
    } finally {
      restoreOriginalImages(convertedImages);
      setIsCapturing(false);
    }
  };

  const saveAsImage = () => {
    const dashboardData = {
      timestamp: new Date().toISOString(),
      roomNumber: roomData?.roomId,
      photos: Object.keys(photos).length,
      damageAnalysis: damageAnalysis,
      issueResolutions: issueResolutions,
      statistics: {
        totalAreas: INSPECTION_AREAS.length,
        capturedAreas: Object.keys(photos).length,
        damagedItems: damagedItemsCount,
        acknowledgedIssues: Object.values(issueResolutions).filter(r => r.acknowledged).length
      }
    };
    
    const dataStr = JSON.stringify(dashboardData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportName = `audit_report_room${roomData?.roomId}_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportName);
    linkElement.click();
    
    showToast('✅ Audit data exported successfully!', 'success');
  };

  useEffect(() => {
    const saved = localStorage.getItem('audit_screenshots');
    if (saved) {
      try {
        setSavedScreenshots(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load screenshots:', e);
      }
    }
  }, []);

  const analyzeImageForDamage = async (areaId, imageFile) => {
    setProcessingImages(prev => ({ ...prev, [areaId]: true }));
    setValidationErrors(prev => ({ ...prev, [areaId]: null }));
    
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    if (PRECONFIGURED_ISSUES[areaId]) {
      const config = PRECONFIGURED_ISSUES[areaId];
      const analysis = {
        areaId,
        areaName: INSPECTION_AREAS.find(a => a.id === areaId).name,
        status: 'damaged',
        severity: config.severity,
        confidence: 0.92,
        detectedIssues: config.issues,
        timestamp: new Date().toISOString(),
        requiresAction: true,
        recommendation: config.recommendation,
        processedBy: 'AI Vision System v2.0',
        processingTime: (Math.random() * 1.5 + 0.5).toFixed(2),
        isPreconfigured: true
      };
      
      setDamageAnalysis(prev => ({ ...prev, [areaId]: analysis }));
      setProcessingImages(prev => ({ ...prev, [areaId]: false }));
      return analysis;
    }
    
    const damagePatterns = DAMAGE_PATTERNS[areaId] || [];
    const hasDamage = Math.random() < 0.3;
    const severity = hasDamage ? ['minor', 'moderate', 'severe'][Math.floor(Math.random() * 3)] : null;
    
    let detectedIssues = [];
    if (hasDamage) {
      const issueCount = Math.floor(Math.random() * 3) + 1;
      for (let i = 0; i < issueCount; i++) {
        const pattern = damagePatterns[Math.floor(Math.random() * damagePatterns.length)];
        if (pattern) detectedIssues.push(pattern);
      }
    }
    
    const analysis = {
      areaId,
      areaName: INSPECTION_AREAS.find(a => a.id === areaId).name,
      status: hasDamage ? 'damaged' : 'good',
      severity: severity,
      confidence: hasDamage ? 0.75 + Math.random() * 0.2 : 0.85 + Math.random() * 0.1,
      detectedIssues: detectedIssues,
      timestamp: new Date().toISOString(),
      requiresAction: hasDamage,
      recommendation: hasDamage 
        ? `${severity.toUpperCase()} damage detected. ${detectedIssues.join(', ')}. Maintenance required.`
        : 'Item appears to be in good condition.',
      processedBy: 'AI Vision System v2.0',
      processingTime: (Math.random() * 1.5 + 0.5).toFixed(2),
      isPreconfigured: false
    };
    
    setDamageAnalysis(prev => ({ ...prev, [areaId]: analysis }));
    setProcessingImages(prev => ({ ...prev, [areaId]: false }));
    
    return analysis;
  };

  const handleIssueAcknowledgment = async (areaId, acknowledged, notes = '') => {
    // Update state
    setIssueResolutions(prev => ({
      ...prev,
      [areaId]: { acknowledged, notes }
    }));
    
    // Send notification to manager immediately when acknowledged
    if (acknowledged) {
      const resolution = {
        acknowledged: true,
        notes: notes,
        timestamp: new Date().toISOString(),
        areaId: areaId,
        areaName: INSPECTION_AREAS.find(a => a.id === areaId)?.name,
        detectedIssues: damageAnalysis[areaId]?.detectedIssues || [],
        severity: damageAnalysis[areaId]?.severity || 'unknown'
      };
      
      await sendAcknowledgementToManager(areaId, resolution);
      showToast(`📧 ${INSPECTION_AREAS.find(a => a.id === areaId)?.name} issue acknowledged & manager notified`, 'success');
    }
    
    // Reset upload triggered flag
    setUploadTriggered(false);
    setUploadStatus({});
  };

  const validateImageForArea = async (areaId, imageFile) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const isValid = Math.random() > 0.05;
    
    if (!isValid) {
      setValidationErrors(prev => ({
        ...prev,
        [areaId]: `The uploaded image does not appear to be a ${INSPECTION_AREAS.find(a => a.id === areaId)?.name}. Please upload the correct photo.`
      }));
      return false;
    }
    return true;
  };

  const handlePhotoCapture = async (areaId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = async (e) => {
      if (e.target.files[0]) {
        const file = e.target.files[0];
        
        const isValid = await validateImageForArea(areaId, file);
        if (!isValid) return;
        
        const photoUrl = URL.createObjectURL(file);
        setPhotos(prev => ({ ...prev, [areaId]: { url: photoUrl, timestamp: new Date().toISOString(), file } }));
        await analyzeImageForDamage(areaId, file);
        setShowAnalysisTable(true);
        setUploadTriggered(false);
      }
    };
    input.click();
  };

  const handleRemovePhoto = (areaId) => {
    if (photos[areaId]?.url && photos[areaId].url.startsWith('blob:')) {
      URL.revokeObjectURL(photos[areaId].url);
    }
    
    setPhotos(prev => {
      const newPhotos = { ...prev };
      delete newPhotos[areaId];
      return newPhotos;
    });
    setDamageAnalysis(prev => {
      const newAnalysis = { ...prev };
      delete newAnalysis[areaId];
      return newAnalysis;
    });
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[areaId];
      return newErrors;
    });
    setUploadTriggered(false);
    setUploadStatus({});
  };

  const handleReanalyze = async (areaId) => {
    if (photos[areaId]?.file) {
      await analyzeImageForDamage(areaId, photos[areaId].file);
      setUploadTriggered(false);
    }
  };

  const handleStaffCheckout = () => {
    if (isReadyForUpload && uploadStatus.success) {
      onFinish();
    } else if (isReadyForUpload && !uploadStatus.success) {
      showToast('Upload in progress. Please wait...', 'error');
    }
  };

  const statistics = {
    total: Object.keys(damageAnalysis).length,
    damaged: damagedItemsCount,
    good: Object.values(damageAnalysis).filter(a => a?.status === 'good').length,
    pending: Object.keys(photos).length - Object.keys(damageAnalysis).length,
    averageConfidence: Object.values(damageAnalysis).reduce((acc, curr) => acc + (curr?.confidence || 0), 0) / (Object.keys(damageAnalysis).length || 1)
  };

  const deleteScreenshot = (id) => {
    const updated = savedScreenshots.filter(s => s.id !== id);
    setSavedScreenshots(updated);
    localStorage.setItem('audit_screenshots', JSON.stringify(updated));
    showToast('Screenshot deleted', 'success');
  };

  const downloadScreenshot = (url, filename) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = url;
    link.click();
    showToast('Download started', 'success');
  };

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-fade-in-up">
          <div className={`${
            toastMessage.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2`}>
            <span>{toastMessage.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={dashboardRef}>
        {/* Screenshot Toolbar */}
       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-6 sticky top-0 z-40">
  {/* Mobile Layout (stacked) */}
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
    
    {/* Buttons Group - Responsive */}
    <div className="flex flex-wrap gap-2">
      {/* Screenshot Button */}
      <button
        onClick={captureScreenshot}
        disabled={isCapturing}
        className={`
          flex items-center justify-center gap-1.5 sm:gap-2 
          px-3 sm:px-4 py-1.5 sm:py-2 
          rounded-lg font-medium transition-all 
          text-sm sm:text-base
          ${isCapturing 
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
            : 'bg-purple-600 text-white hover:bg-purple-700'
          }
        `}
      >
        {isCapturing ? (
          <>
            <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
            <span className="text-xs sm:text-sm">Capturing...</span>
          </>
        ) : (
          <>
            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="hidden xs:inline text-xs sm:text-sm">Save Screenshot</span>
            <span className="xs:hidden text-xs sm:text-sm">Capture</span>
          </>
        )}
      </button>
      
      {/* Export Data Button */}
      <button
        onClick={saveAsImage}
        className="
          flex items-center justify-center gap-1.5 sm:gap-2 
          px-3 sm:px-4 py-1.5 sm:py-2 
          rounded-lg font-medium 
          bg-green-600 text-white hover:bg-green-700 
          transition-all text-sm sm:text-base
        "
      >
        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        <span className="hidden xs:inline text-xs sm:text-sm">Export Data</span>
        <span className="xs:hidden text-xs sm:text-sm">Export</span>
      </button>
      
      {/* Gallery Button */}
      {savedScreenshots.length > 0 && (
        <button
          onClick={() => setShowScreenshotGallery(!showScreenshotGallery)}
          className="
            flex items-center justify-center gap-1.5 sm:gap-2 
            px-3 sm:px-4 py-1.5 sm:py-2 
            rounded-lg font-medium 
            bg-blue-50 text-blue-700 hover:bg-blue-100 
            transition-all text-sm sm:text-base
          "
        >
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="hidden xs:inline text-xs sm:text-sm">Gallery ({savedScreenshots.length})</span>
          <span className="xs:hidden text-xs sm:text-sm">📸 {savedScreenshots.length}</span>
        </button>
      )}
    </div>
    
    {/* Tip Text - Responsive */}
    <div className="
      text-center sm:text-right 
      text-xs text-gray-500 
      flex items-center justify-center sm:justify-end
      gap-1
    ">
      <span className="text-base sm:text-lg">💡</span>
      <span className="hidden sm:inline text-xs">Tip: Acknowledge issues to automatically notify manager</span>
      <span className="sm:hidden text-[11px]">Acknowledge issues to notify manager</span>
    </div>
    
  </div>
</div>

        {screenshotError && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">{screenshotError}</p>
          </div>
        )}

        {/* Screenshot Gallery Modal */}
        {showScreenshotGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setShowScreenshotGallery(false)}>
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Saved Screenshots
                </h3>
                <button onClick={() => setShowScreenshotGallery(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {savedScreenshots.length === 0 ? (
                  <div className="text-center text-gray-500 py-12">
                    No screenshots saved yet. Click the "Save Screenshot" button to capture the current view.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {savedScreenshots.map((screenshot) => (
                      <div key={screenshot.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-all">
                        <img 
                          src={screenshot.url} 
                          alt={`Screenshot ${new Date(screenshot.timestamp).toLocaleString()}`}
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => window.open(screenshot.url, '_blank')}
                        />
                        <div className="p-3">
                          <div className="text-xs text-gray-500 mb-2">
                            <div>📅 {new Date(screenshot.timestamp).toLocaleString()}</div>
                            <div>🏠 Room {screenshot.roomNumber}</div>
                            <div>📸 {screenshot.totalAreas} areas | ⚠️ {screenshot.damagedItems} damages</div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => downloadScreenshot(screenshot.url, `audit_room${screenshot.roomNumber}_${screenshot.id}.png`)}
                              className="flex-1 bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 transition-all"
                            >
                              Download
                            </button>
                            <button
                              onClick={() => deleteScreenshot(screenshot.id)}
                              className="flex-1 bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 transition-all"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Audit & Checkout</h2>
          <p className="text-sm text-gray-500 mt-1">Room {roomData?.roomId} • AI-Powered Visual Inspection System</p>
        </div>

        {/* Progress Indicator */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Photo Documentation Progress</span>
            <span className="text-sm font-semibold text-blue-600">
              {Object.keys(photos).length}/{INSPECTION_AREAS.length} Areas
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(Object.keys(photos).length / INSPECTION_AREAS.length) * 100}%` }}
            />
          </div>
          
          {/* Pending Acknowledgments Display */}
          {allPhotosCaptured && allPhotosProcessed && pendingAcknowledgments.length > 0 && (
            <div className="mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-800 font-semibold flex items-center gap-1">
                <span>⚠️</span> 
                Pending Acknowledgments: {pendingAcknowledgments.map(a => INSPECTION_AREAS.find(area => area.id === a)?.name).join(', ')}
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                Please acknowledge these issues to notify the manager and complete the audit
              </p>
            </div>
          )}
          
          {!allPhotosCaptured && (
            <p className="text-xs text-amber-600 mt-2">⚠️ Please capture photos of all required areas before checkout</p>
          )}
          {allPhotosCaptured && !allPhotosProcessed && (
            <p className="text-xs text-blue-600 mt-2">🤖 AI analyzing images... Please wait for processing to complete</p>
          )}
          {hasValidationErrors && (
            <p className="text-xs text-red-600 mt-2">❌ Some images failed validation. Please re-upload the correct photos for the highlighted areas.</p>
          )}
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {INSPECTION_AREAS.map((area) => {
            const isProblematic = problematicAreas.includes(area.id);
            const isResolved = issueResolutions[area.id]?.acknowledged;
            
            return (
              <div
                key={area.id}
                className={`bg-white rounded-lg shadow-sm border transition-all ${
                  photos[area.id] 
                    ? damageAnalysis[area.id]?.status === 'damaged'
                      ? isResolved 
                        ? 'border-yellow-300 bg-yellow-50' 
                        : 'border-red-300 bg-red-50 ring-2 ring-red-400 ring-opacity-50'
                      : validationErrors[area.id]
                        ? 'border-orange-300 bg-orange-50 ring-2 ring-orange-400 ring-opacity-50'
                        : 'border-green-300 bg-green-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{area.icon}</span>
                      <span className="font-medium text-gray-800 text-sm">{area.name}</span>
                    </div>
                    {area.required && <span className="text-xs text-red-500">*</span>}
                    {processingImages[area.id] && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}
                    {isProblematic && photos[area.id] && damageAnalysis[area.id] && !isResolved && (
                      <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full animate-pulse">ACTION NEEDED</span>
                    )}
                    {isProblematic && isResolved && (
                      <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">ACKNOWLEDGED</span>
                    )}
                  </div>

                  {photos[area.id] ? (
                    <div className="relative">
                      <img
                        src={photos[area.id].url}
                        alt={area.name}
                        className={`w-full h-32 object-cover rounded-lg border cursor-pointer ${
                          damageAnalysis[area.id]?.status === 'damaged' && !isResolved
                            ? 'border-red-500 border-2'
                            : damageAnalysis[area.id]?.status === 'damaged' && isResolved
                              ? 'border-yellow-500 border-2'
                              : validationErrors[area.id]
                                ? 'border-orange-500 border-2'
                                : 'border-green-500 border-2'
                        }`}
                        onClick={() => setSelectedArea({ ...area, url: photos[area.id].url })}
                      />
                      <button
                        onClick={() => handleRemovePhoto(area.id)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      
                      {damageAnalysis[area.id] && !processingImages[area.id] && !validationErrors[area.id] && (
                        <div className={`absolute bottom-1 left-1 px-2 py-0.5 rounded text-xs font-semibold ${
                          damageAnalysis[area.id].status === 'damaged' 
                            ? isResolved ? 'bg-yellow-500 text-white' : 'bg-red-500 text-white' 
                            : 'bg-green-500 text-white'
                        }`}>
                          {damageAnalysis[area.id].status === 'damaged' ? (isResolved ? '⚠️ Damage (Acknowledged)' : '⚠️ Damage Detected') : '✓ OK'}
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-400 mt-1 flex justify-between items-center">
                        <span>Captured: {new Date(photos[area.id].timestamp).toLocaleTimeString()}</span>
                        {damageAnalysis[area.id] && !processingImages[area.id] && (
                          <button
                            onClick={() => handleReanalyze(area.id)}
                            className="text-blue-500 hover:text-blue-700 text-xs"
                          >
                            Re-analyze
                          </button>
                        )}
                      </div>
                      
                      {/* Issue Acknowledgment Section */}
                      {isProblematic && damageAnalysis[area.id] && damageAnalysis[area.id].status === 'damaged' && !isResolved && (
                        <div className="mt-2 p-2 bg-red-100 rounded-lg">
                          <p className="text-xs text-red-700 font-semibold mb-1">🚨 Issues Detected:</p>
                          <p className="text-xs text-red-600 mb-2">{damageAnalysis[area.id].detectedIssues.join(', ')}</p>
                          <p className="text-xs text-red-600 mb-2 font-semibold">Severity: {damageAnalysis[area.id].severity?.toUpperCase()}</p>
                          <button
                            onClick={() => {
                              const notes = prompt('Please enter maintenance notes or resolution plan:', 'Needs repair/replacement');
                              if (notes !== null) {
                                handleIssueAcknowledgment(area.id, true, notes);
                              }
                            }}
                            className="w-full bg-red-600 text-white text-xs py-1 rounded hover:bg-red-700 transition flex items-center justify-center gap-1"
                          >
                            <span>📧</span>
                            Acknowledge & Notify Manager
                          </button>
                        </div>
                      )}
                      
                      {isProblematic && isResolved && (
                        <div className="mt-2 p-2 bg-green-100 rounded-lg">
                          <p className="text-xs text-green-700 font-semibold flex items-center gap-1">
                            <span>✅</span> Issue Acknowledged & Manager Notified
                          </p>
                          <p className="text-xs text-green-600 mt-1">📝 Notes: {issueResolutions[area.id]?.notes || 'No notes provided'}</p>
                          <p className="text-xs text-green-500 mt-1">📧 Notification sent to manager at {new Date().toLocaleTimeString()}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handlePhotoCapture(area.id)}
                      className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50 transition group"
                    >
                      <svg className="w-8 h-8 text-gray-400 group-hover:text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm text-gray-500 group-hover:text-blue-600">Capture Photo</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* AI Analysis Table */}
        {showAnalysisTable && Object.keys(damageAnalysis).length > 0 && (
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 mb-6 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    AI Vision Analysis Dashboard
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">Powered by Advanced Machine Learning • Real-time Damage Detection</p>
                </div>
                <div className="flex gap-2">
                  <div className="bg-white/20 rounded-lg px-3 py-1 text-center">
                    <div className="text-white text-2xl font-bold">{statistics.damaged}</div>
                    <div className="text-blue-100 text-xs">Damaged Items</div>
                  </div>
                  <div className="bg-white/20 rounded-lg px-3 py-1 text-center">
                    <div className="text-white text-2xl font-bold">{(statistics.averageConfidence * 100).toFixed(1)}%</div>
                    <div className="text-blue-100 text-xs">Avg Confidence</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Area</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Confidence</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Detected Issues</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Recommendation</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Action Required</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Resolution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {INSPECTION_AREAS.filter(area => damageAnalysis[area.id]).map((area) => {
                    const analysis = damageAnalysis[area.id];
                    const isResolved = issueResolutions[area.id]?.acknowledged;
                    
                    return (
                      <tr key={area.id} className={`hover:bg-gray-50 transition ${
                        analysis?.status === 'damaged' && !isResolved ? 'bg-red-50' : 
                        analysis?.status === 'damaged' && isResolved ? 'bg-yellow-50' :
                        'bg-green-50/30'
                      }`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{area.icon}</span>
                            <span className="font-medium text-gray-800">{area.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            analysis?.status === 'damaged' && !isResolved ? 'bg-red-100 text-red-700' :
                            analysis?.status === 'damaged' && isResolved ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {analysis?.status === 'damaged' ? (isResolved ? '🟡 Damaged (Ack)' : '🔴 Damaged') : '🟢 Good'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${analysis?.confidence > 0.8 ? 'bg-green-500' : analysis?.confidence > 0.6 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${(analysis?.confidence || 0) * 100}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-600">{((analysis?.confidence || 0) * 100).toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {analysis?.status === 'damaged' ? (
                            <div className="flex flex-wrap gap-1">
                              {analysis?.detectedIssues?.map((issue, idx) => (
                                <span key={idx} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                  {issue}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-500">No issues detected</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-700">{analysis?.recommendation?.substring(0, 60)}...</span>
                        </td>
                        <td className="px-4 py-3">
                          {analysis?.requiresAction ? (
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                              <span className="text-xs font-semibold text-red-600">Maintenance Required</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              <span className="text-xs font-semibold text-green-600">Ready for Guest</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isResolved ? (
                            <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
                              <span>✅</span> Resolved & Notified
                            </span>
                          ) : analysis?.status === 'damaged' ? (
                            <span className="text-xs text-red-600">Pending Acknowledgment</span>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Upload Status - Shows progress and auto-upload */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload Status & Manager Notifications
          </h3>
          
          {isUploading ? (
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Uploading data to server and notifying manager...</span>
            </div>
          ) : uploadStatus.success ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span>{uploadStatus.message}</span>
              </div>
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                <div>📅 Timestamp: {new Date().toISOString()}</div>
                <div>🏠 Room: {roomData?.roomId} | 👤 StaffID: HK1001</div>
                <div>📸 Photos: {Object.keys(photos).length} captured</div>
                <div>🤖 AI Analysis: {statistics.total} images processed | {damagedItemsCount} issues found</div>
                <div>✅ Resolved: {Object.values(issueResolutions).filter(r => r.acknowledged).length}/{problematicAreas.length} issues acknowledged</div>
                <div>📧 Manager Notification: Sent successfully to maintenance team</div>
              </div>
            </div>
          ) : (
            <div>
              {/* <p className="text-gray-500 text-sm mb-2">
                {pendingAcknowledgments.length > 0 
                  ? `⏳ Waiting for ${pendingAcknowledgments.length} issue(s) to be acknowledged...`
                  : "Waiting for all photos to be captured and analyzed..."}
              </p> */}
              {pendingAcknowledgments.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800">
                    💡 Once you acknowledge all issues, the system will automatically:
                  </p>
                  <ul className="text-xs text-blue-700 mt-1 list-disc list-inside">
                    <li>Upload all inspection data to the server</li>
                    <li>Send notifications to the manager</li>
                    <li>Create maintenance tickets for damaged items</li>
                    <li>Mark the room as ready for checkout</li>
                  </ul>
                </div>
              )}
              {isReadyForUpload && !uploadStatus.success && !isUploading && (
                <button
                  onClick={() => {
                    setUploadTriggered(false);
                    setUploadStatus({});
                  }}
                  className="mt-2 text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition"
                >
                  Retry Upload
                </button>
              )}
            </div>
          )}
        </div>

        {/* Checkout Button */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <button
            onClick={handleStaffCheckout}
            disabled={!isReadyForUpload || !uploadStatus.success}
            className={`px-8 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 mx-auto ${
              isReadyForUpload && uploadStatus.success
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Staff Checkout & Complete
          </button>
          <p className="text-xs text-gray-400 mt-3">
            {!allPhotosCaptured 
              ? "📸 Please capture photos of all 11 required inspection areas"
              : !allPhotosProcessed 
                ? "🤖 AI is analyzing all images. Please wait..."
                : hasValidationErrors
                  ? "❌ Please fix validation errors before completing checkout"
                  : !allProblematicAreasAnalyzed
                    ? "⚠️ Waiting for analysis of Curtains, Iron Unit, Towels, and Couch"
                    : !allIssuesAcknowledged()
                      ? `⚠️ Please acknowledge ${pendingAcknowledgments.length} issue(s) to notify manager and complete checkout`
                      : !uploadStatus.success
                        ? "📤 Auto-uploading data to server..."
                        : "✅ All inspections complete. Room ready for next guest."}
          </p>
        </div>

        {/* Modal for full-size image preview */}
        {selectedArea && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => setSelectedArea(null)}>
            <div className="bg-white rounded-xl max-w-lg w-full p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold">{selectedArea.name}</h3>
                <button onClick={() => setSelectedArea(null)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
              </div>
              <img src={selectedArea.url} alt={selectedArea.name} className="w-full rounded-lg" />
              {damageAnalysis[selectedArea.id] && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">{damageAnalysis[selectedArea.id].recommendation}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}